import React, { useCallback, useState } from 'react';
import type { ErrorLike, SingleShipmentDataExtended } from '@/apis/types';
import { getProductDDL } from '@/common/utils/dropDown';
import { usePaginatedProductDropdown } from '@/hooks/usePaginatedProductDropdown';
import { ProductPicker } from './components';
import {
  IMPORTER_PRODUCTS_NOTE,
  PIECES_PRODUCTS_NOTE,
} from './components/EditProductsSection';
import {
  initialSensorRowsFromShipment,
  type SensorFormRow,
  type SensorSelectRow,
} from './sensorFormPayload';
import { useEditProductLines } from './useEditProductLines';
import { useSensorProductLinks } from './useSensorProductLinks';
import {
  pickerCatalogProps,
  type PickerCatalogProps,
} from './productPickerCatalog';

interface UseEditSensorsAndProductsOptions {
  shipmentId: string;
  shipment: SingleShipmentDataExtended | null;
  sensorsList: SensorSelectRow[];
  token: string;
  unauthorize: (error?: ErrorLike) => void;
  enabled: boolean;
  openProductForm: () => void;
}

const toErrorLike = (error: unknown): ErrorLike =>
  typeof error === 'object' &&
  error !== null &&
  'status' in error &&
  typeof error.status === 'number'
    ? { status: error.status }
    : {};

/** Initial sensor rows for the edit drawer, each with the products it's linked to. */
export function sensorRowsWithProducts(
  shipment: Partial<
    Pick<SingleShipmentDataExtended, 'sensors' | 'teams'>
  > | null,
  sensorsList: SensorSelectRow[]
): SensorFormRow[] {
  if (!shipment) return [];
  const productIdsByTrackingId = new Map(
    (shipment.sensors ?? []).map((sensor) => [
      String(sensor.sensor_tracking_id ?? ''),
      (sensor.products ?? []).map((product) => product.id),
    ])
  );
  return initialSensorRowsFromShipment(shipment, sensorsList).map((row) => ({
    ...row,
    product_ids:
      productIdsByTrackingId.get(String(row.sensor_tracking_id ?? '')) ?? [],
  }));
}

/** The edit drawer's Products and Sensors state, as `EditProductsAndSensors` and the save read it: lines, sensor rows, the shared picker, and the save payload (spec UE2–UE6). */
export function useEditSensorsAndProducts({
  shipmentId,
  shipment,
  sensorsList,
  token,
  unauthorize,
  enabled,
  openProductForm,
}: UseEditSensorsAndProductsOptions) {
  const [productsRefreshKey, setProductsRefreshKey] = useState(0);
  const unauthorizeCatalog = useCallback(
    (error: unknown) => unauthorize(toErrorLike(error)),
    [unauthorize]
  );
  const fromPieces = (shipment?.pieces?.length ?? 0) > 0;
  const products = useEditProductLines(shipmentId, enabled, fromPieces);
  const catalog = usePaginatedProductDropdown({
    token,
    enabled: enabled && !products.readOnly,
    productsRefreshKey,
    unauthorize: unauthorizeCatalog,
  });

  const knownNames = new Map<string, string>();
  for (const sensor of shipment?.sensors ?? []) {
    for (const product of sensor.products ?? []) {
      knownNames.set(product.id, product.name);
    }
  }
  for (const row of products.rows) {
    if (row.product_id && row.name) knownNames.set(row.product_id, row.name);
  }

  const getProductName = (id: string): string =>
    catalog.getProductName(id) || knownNames.get(id) || '';

  const initialSensorRows = sensorRowsWithProducts(shipment, sensorsList);

  const shipmentProducts = products.rows.flatMap((row) =>
    row.product_id ? [{ id: row.product_id, name: row.name ?? '' }] : []
  );

  const links = useSensorProductLinks({
    selectedProducts: products.lines,
    setSelectedProducts: products.setLines,
    getProductName,
    mergeIntoLookup: catalog.mergeIntoLookup,
    loadCatalog: products.readOnly
      ? () => Promise.resolve(shipmentProducts)
      : () => getProductDDL(token),
    openProductForm,
    initialSensorRows,
    sessionKey: shipmentId,
  });

  const catalogProps = pickerCatalogProps(catalog);
  const pickerCatalog: PickerCatalogProps = !products.ready
    ? {
        ...catalogProps,
        options: [],
        catalogTotalCount: 0,
        loading: products.isLoading,
      }
    : products.readOnly
      ? {
          ...catalogProps,
          options: shipmentProducts.filter((product) =>
            product.name
              .toLowerCase()
              .includes(catalog.search.trim().toLowerCase())
          ),
          catalogTotalCount: shipmentProducts.length,
          loading: false,
        }
      : catalogProps;

  const renderProductPicker = (
    value: string[],
    onChange: (ids: string[]) => void,
    ariaLabel: string
  ): React.ReactNode => (
    <ProductPicker
      ariaLabel={ariaLabel}
      selectedIds={value}
      onChange={onChange}
      {...pickerCatalog}
      getProductName={getProductName}
      loadAllProductIds={
        products.ready ? links.loadAllProductIds : () => Promise.resolve([])
      }
      onOpenAddProduct={
        !products.ready || products.readOnly
          ? undefined
          : () => links.openAddProduct((id) => onChange([...value, id]))
      }
    />
  );

  const finishAddProduct = (created?: { id: string; name: string }): void => {
    setProductsRefreshKey((key) => key + 1);
    links.finishAddProduct(created);
  };

  return {
    products,
    catalog: catalogProps,
    getProductName,
    renderProductPicker,
    finishAddProduct,
    sensorRows: links.sensorRows,
    changeSensorRows: links.changeSensorRows,
    changeShipmentProducts: links.changeShipmentProducts,
    sensorCardOpen: links.sensorCardOpen,
    setSensorCardOpen: links.setSensorCardOpen,
    afterSave: products.afterSave,
    productsNote: products.readOnly
      ? fromPieces
        ? PIECES_PRODUCTS_NOTE
        : IMPORTER_PRODUCTS_NOTE
      : undefined,
    sendProductIds: (): boolean =>
      links.sensorRowsEdited || products.payload() !== undefined,
  };
}

/** What `useEditSensorsAndProducts` hands the edit drawer. */
export type EditSensorsAndProducts = ReturnType<
  typeof useEditSensorsAndProducts
>;
